<template>
  <div
    v-bind:id="elementId.root"
    class="input-text form-control"
    v-bind:class="{
      'is-valid': state,
      'is-invalid': state === false,
      'has-template': hasTemplate,
      disabled,
    }"
    v-on:focusin="$_abortTimeout"
  >
    <b-form-textarea
      v-bind:id="elementId.form"
      class="text-area"
      v-bind:class="{ 'has-template': hasTemplate }"
      ref="textarea"
      no-resize
      v-model="modelValue"
      v-bind:rows="defaultRows"
      v-bind:max-rows="maxRows"
      v-bind:disabled="disabled"
      v-bind:placeholder="placeholder"
      v-on:focus="$_onFocusTextarea"
      v-on:blur="$_onBlurTextarea"
    />
    <div v-if="hasTemplate" class="selector-wrapper">
      <input-text-template-selector
        ref="selector"
        v-bind:id="elementId.template"
        v-bind:templates="templates"
        v-on:selected="$_onTemplateSelected"
      />
    </div>
  </div>
</template>

<script>
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import InputTextTemplateSelector from '@/components/AppInputTextTemplateSelector.vue'

/**
 * 挿入位置enum
 * @type {Object}
 */
const INSERT_POSITION = {
  CARET: 'caret',
  END: 'end',
}

export default {
  name: 'AppInputText',
  components: {
    'input-text-template-selector': InputTextTemplateSelector,
  },
  props: {
    /**
     * 値
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * 定型文
     *
     * ```
     * [{ id:1, text_template: 'foo' }, { id: 2, text_template: 'bar' }, ...]
     * ```
     */
    templates: {
      type: Array,
      default: () => [],
    },
    /**
     * バリデーション状態
     */
    state: {
      type: Boolean,
      default: null,
    },
    /**
     * 非活性状態
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * プレースホルダー
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * IDのprefix
     */
    idPrefix: {
      type: String,
      default: '',
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // v-model用value
      modelValue: this.value,
      // 初期行数
      defaultRows: 4,
      // 最大行数(実質的な表示行数)
      maxRows: 10,
      // テキスト挿入位置
      insertPosition: INSERT_POSITION.END,
      // 選択側イベント監視用timeoutID
      timeoutId: null,
    }
  },
  computed: {
    /**
     * テンプレート選択がある場合はtrue
     * @type {Boolean}
     */
    hasTemplate: function () {
      return !this.disabled && this.templates.length > 0
    },
    /**
     * 要素ID
     */
    elementId: function () {
      return {
        root: this.idPrefix ? `${this.idPrefix}-input-text` : 'input-text',
        form: this.idPrefix
          ? `${this.idPrefix}-input-text-form`
          : 'input-text-form',
        template: this.idPrefix
          ? `${this.idPrefix}-input-text-template-selector`
          : 'input-text-template-selector',
      }
    },
  },
  watch: {
    value: function (value) {
      this.$_setModelValue(value)
    },
    modelValue: function () {
      /**
       * 値変更イベント
       * @param {String} value 値
       */
      this.$emit('changed', this.modelValue)
    },
  },
  mounted: function () {
    this.$log.debug('AppInputText mounted!')
  },
  methods: {
    /**
     * 値を設定する
     * @param {String} value 値
     */
    $_setModelValue: function (value) {
      this.modelValue =
        typeof value === 'string' ? value.replace(/\\n/g, '\n') : value
    },

    /**
     * テキストエリアfocus時処理
     */
    $_onFocusTextarea: function () {
      if (!this.templates.length) return
      this.insertPosition = INSERT_POSITION.CARET
    },

    /**
     * テキストエリアblur時処理
     */
    $_onBlurTextarea: function () {
      if (!this.templates.length) return

      // テンプレート挿入位置を末尾に設定する
      // ただしテンプレート選択からイベントが上がる場合は元の値を維持したいので
      // timeoutで少し待ってその間だけlistenerを設定し、callbackでtimeoutをクリアする
      const selector = this.$refs['selector']
      if (selector) {
        selector.$on('selected', this.$_abortTimeout)
        selector.$on('list-show', this.$_abortTimeout)
        this.timeoutId = setTimeout(() => {
          if (!selector) return
          selector.$off('selected', this.$_abortTimeout)
          selector.$off('list-show', this.$_abortTimeout)
          this.insertPosition = INSERT_POSITION.END
          this.timeoutId = null
        }, 100) //FIXME:待ち時間がマジックナンバー
      } else {
        this.insertPosition = INSERT_POSITION.END
      }
    },

    /**
     * 挿入位置を末尾に移動するtimeout処理を中断する
     */
    $_abortTimeout: function () {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    },

    /**
     * 文字列テンプレート選択時処理
     * @param {Object} template 文字列テンプレート
     */
    $_onTemplateSelected: function (template) {
      this.$_insertText(template.text_template)
      /**
       * 文字列テンプレート使用イベント
       * @param {Object} template 文字列テンプレート
       */
      this.$emit('text-template-used', template)
    },

    /**
     * 文字列テンプレートを挿入する
     * @param {Object} text 挿入する文字列
     */
    $_insertText: function (text) {
      if (!this.modelValue) {
        // 入力値が無いので単純に置き換え
        this.$log.debug('[DEBUG] replace to text', text)
        this.modelValue = text
      } else if (this.insertPosition === INSERT_POSITION.CARET) {
        // キャレット位置に挿入
        const element = this.$refs['textarea'].$el
        const index = element.selectionStart
        this.$log.debug('[DEBUG] insert text to caret', index, text)
        this.modelValue =
          this.modelValue.slice(0, index) + text + this.modelValue.slice(index)
        // フォーカスを戻して挿入後の位置にキャレットを移動
        element.focus()
        this.$nextTick(() => {
          element.setSelectionRange(index + text.length, index + text.length)
        })
      } else if (this.insertPosition === INSERT_POSITION.END) {
        // 末尾に挿入
        this.$log.debug('[DEBUG] append text', text)
        this.modelValue = this.modelValue + text
      } else {
        this.$log.debug('[DEBUG] insert position invalid', this.insertPosition)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.input-text {
  width: 100%;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;

  &.form-control {
    height: auto;

    &.is-valid,
    &.is-invalid {
      background-image: none;
      padding: 0.375rem !important;
    }
  }

  &:focus-within {
    outline: 0;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgb(0 123 255 / 25%);

    &.is-valid {
      border-color: #28a745;
      box-shadow: 0 0 0 0.2rem rgb(40 167 69 / 25%);
    }

    &.is-invalid {
      border-color: #dc3545;
      box-shadow: 0 0 0 0.2rem rgb(220 53 69 / 25%);
    }
  }

  &.has-template {
    padding: 0.375rem;
  }

  &.disabled {
    background-color: #e9ecef;
  }
}

.text-area {
  border: none;
  @include mixin-scrollbar;

  &:focus {
    box-shadow: none;
  }

  &.has-template,
  .is-valid &,
  .is-invalid & {
    padding: 0 0.375rem;
  }
}

.selector-wrapper {
  width: calc(100% - 0.625rem * 2);
  margin-top: 0.5rem;
  padding: 1rem 8px 0.625rem 8px;
  border-top: 1px solid $button-border-color;
}
</style>
